import DotsHorizontalIcon from '@eduzz/houston-icons/DotsHorizontal';
import FilterOutlineIcon from '@eduzz/houston-icons/FilterOutline';
import Input from '@eduzz/houston-ui/Forms/Input';
import { Grid, IconButton, Menu, MenuItem, Paper } from '@material-ui/core';
import ModalActions from 'components/ModalActions';
import Spin from 'components/Spin';
import type { MouseEvent } from 'react';
import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import accessControlService from 'v2/services/access-control';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import type { Action, Group } from 'v2/interfaces/access-control';

import { AccessPolicyIcon } from './Icon';
import { useStyles } from './styles';

type GroupsProps = {
  onEdit: (group: Group) => void;
};

type GroupPrivilege = {
  name: string;
  actions: Action[];
};

function getGroupPrivileges(group: Group): GroupPrivilege[] {
  return Object.values(group.privileges)
    .filter((privilege) => privilege.actions.length > 0)
    .map((privilege) => ({
      name: privilege.name,
      actions: privilege.actions,
    }));
}

function Groups({ onEdit }: GroupsProps) {
  const classes = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [listGroupsTrigger, setListGroupsTrigger] = useState(false);
  useObservable(() => accessControlService.loadGroups(), [listGroupsTrigger]);
  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState({} as Group);
  const [deleteGroup] = useCallbackObservable((groupId) => {
    setIsDeleting(true);
    return accessControlService.deleteGroup(groupId).pipe(
      tap(() => {
        toast.success('Sucesso, grupo removido');
        setListGroupsTrigger((prev) => !prev);
        handleCloseDeleteConfirmation();
      }),
      catchError(() => {
        toast.error('Erro ao remover grupo, tente novamente.');
        return EMPTY;
      }),
      finalize(() => {
        setIsDeleting(false);
      })
    );
  }, []);

  function handleCloseMenuOptions() {
    setMenuAnchorEl(null);
  }

  function handleDotsClick(e: MouseEvent<HTMLButtonElement>, group: Group) {
    setMenuAnchorEl(e.currentTarget);
    setCurrentGroup(group);
  }

  function handleEditOptionClick() {
    onEdit(currentGroup);
    handleCloseMenuOptions();
  }

  function handleRemoveOptionClick() {
    setIsDeleteConfirmationOpen(true);
    handleCloseMenuOptions();
  }

  function handleCloseDeleteConfirmation() {
    setIsDeleteConfirmationOpen(false);
  }

  function handleDeleteGroup() {
    deleteGroup(currentGroup.id);
  }

  const filteredGroups = useMemo(
    () => (groups || []).filter((group) => (!search || search === '' ? group : group.name.toLowerCase().includes(search))),
    [groups, search]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');
  const shouldShowActions = editPermission || deletePermission;

  return (
    <>
      <Grid container justifyContent='flex-end'>
        <Grid item xs={12} md={4}>
          <div className={classes.search}>
            <Input
              id='search-product-input'
              value={search}
              endAdornment={<FilterOutlineIcon />}
              onChange={setSearch}
              placeholder='Pesquise pelo nome do grupo'
            />
          </div>
        </Grid>
      </Grid>
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleCloseMenuOptions}>
        <MenuItem onClick={handleEditOptionClick}>Editar</MenuItem>
        <MenuItem onClick={handleRemoveOptionClick}>Remover</MenuItem>
      </Menu>

      <ModalActions
        open={isDeleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        onOk={handleDeleteGroup}
        title='Atenção'
        buttonOk='Continuar'
        loading={isDeleting}
      >
        <p>Deseja realmente remover o grupo {currentGroup.name}?</p>
      </ModalActions>

      {groups === null ? (
        <div className={classes.spinContainer}>
          <Spin />
        </div>
      ) : (
        <>
          <Grid container spacing={2}>
            {filteredGroups?.length > 0 ? (
              filteredGroups?.map((group) => {
                const groupPrivileges = getGroupPrivileges(group);
                return (
                  <Grid xs={12} sm={6} md={4} item key={group.id}>
                    <Paper elevation={1} className={classes.card}>
                      {shouldShowActions && group.owner && (
                        <IconButton className={classes.options} onClick={(e) => handleDotsClick(e, group)} size='small'>
                          <DotsHorizontalIcon size={18} />
                        </IconButton>
                      )}
                      <h3 className={classes.title}>{group.name}</h3>
                      <div className={classes.privileges}>
                        {groupPrivileges.map((privilege) => (
                          <div key={privilege.name} className={classes.privilegeContainer}>
                            <p>{privilege.name}</p>
                            <ul>
                              {privilege.actions.map((action) => (
                                <AccessPolicyIcon key={action.id} action={action} />
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </Paper>
                  </Grid>
                );
              })
            ) : (
              <Grid container spacing={2}>
                <Grid xs={12} item>
                  <div className={classes.emptyList}>Nenhum registro encontrado</div>
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
}

export { Groups };
